import { ContextMenuInputPoint, } from '@thoughtspot/chart-common';
import { convertToMilliSeconds } from '@thoughtspot/date-format-util/esm/date-util';
import { create, registerOnErrorCallback } from '@thoughtspot/logger';
import { convertValueToBackend, hasCustomCalendar, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getAllColumnsConfig, } from '../data/data-util';
import { AnswerColumnEffectiveType, } from '../types';
import { getAxisConfig, getYAxisColumnById } from './chart-model-utils';
import { configOptions } from './chart-type-specification-service';
const logger = create('Viz-Context-Menu-Util');
registerOnErrorCallback(_.noop);
export var GenericMenuItemType;
(function (GenericMenuItemType) {
    GenericMenuItemType["SEPARATOR"] = "context-menu-item-separator";
    GenericMenuItemType["PIVOT_HEADER_EXPAND"] = "pivot-context-menu-header-expand";
    GenericMenuItemType["PIVOT_HEADER_COLLAPSE"] = "pivot-context-menu-header-collapse";
    GenericMenuItemType["PIVOT_SORT"] = "pivot-context-menu-sort";
    GenericMenuItemType["PIVOT_CLEAR_SORT"] = "pivot-context-menu-clear-sort";
    GenericMenuItemType["PIVOT_SELECTION_EXPAND"] = "pivot-context-menu-selection-expand";
    GenericMenuItemType["PIVOT_SELECTION_COLLAPSE"] = "pivot-context-menu-selection-collapse";
    GenericMenuItemType["EXPLAIN_ANOMALY"] = "context-menu-item-explain_anomaly";
})(GenericMenuItemType || (GenericMenuItemType = {}));
export const getRawValueForCustomCalendar = (customCalendarMap, column, formattedValue) => {
    if (hasCustomCalendar(column)) {
        return customCalendarMap[formattedValue];
    }
    return convertValueToBackend(column, formattedValue);
};
export const getColumnConfig = (col, allColumnsMap) => {
    if (!(allColumnsMap === null || allColumnsMap === void 0 ? void 0 : allColumnsMap[col.id])) {
        return null;
    }
    return allColumnsMap[col.id];
};
export const getXAxisContextMenuInputValue = (xAxisColumn, value, rawValue, allColumnsConfigMap) => {
    const columnCustomCalendarMap = getColumnConfig(xAxisColumn, allColumnsConfigMap);
    const isXAxisMeasure = xAxisColumn.type === AnswerColumnEffectiveType.Measure;
    return {
        column: xAxisColumn,
        value: isXAxisMeasure
            ? value
            : getRawValueForCustomCalendar(columnCustomCalendarMap === null || columnCustomCalendarMap === void 0 ? void 0 : columnCustomCalendarMap.displayToCustomCalendarValueMap, xAxisColumn, rawValue),
    };
};
export const getYAxisContextMenuInputValue = (yAxisColumn, value, allColumnsConfigMap, yRawValues) => {
    let yAxisSelectedValue;
    if (yAxisColumn.type === AnswerColumnEffectiveType.Measure) {
        yAxisSelectedValue = value;
    }
    else {
        const columnCustomCalendarMap = getColumnConfig(yAxisColumn, allColumnsConfigMap);
        yAxisSelectedValue = getRawValueForCustomCalendar(columnCustomCalendarMap === null || columnCustomCalendarMap === void 0 ? void 0 : columnCustomCalendarMap.displayToCustomCalendarValueMap, yAxisColumn, yRawValues[value]);
    }
    if (!_.isNil(yAxisSelectedValue)) {
        const yContextMenuValue = {
            column: yAxisColumn,
            value: yAxisSelectedValue,
        };
        return yContextMenuValue;
    }
    return null;
};
export function getContextMenuInputPoint(chartModel, chartPoint) {
    var _a;
    const contextMenuInputPoint = new ContextMenuInputPoint();
    const axisConfig = getAxisConfig(chartModel);
    const radialColumn = axisConfig.size;
    if (radialColumn && !_.isNil(chartPoint.z)) {
        const radialContextMenuValue = {
            column: radialColumn,
            value: chartPoint.z,
        };
        contextMenuInputPoint.pushSelected(radialContextMenuValue);
    }
    const valueColumnId = _.get(chartPoint, 'series.userOptions.valueColumnIdentifier', chartPoint.series.valueColumnIdentifier);
    const allColumnsConfigMap = _.get(chartPoint, 'series.chart.userOptions.chart.props.allColumns');
    const yAxisColumn = getYAxisColumnById(chartModel, valueColumnId);
    const yAxisContextMenuInputValue = getYAxisContextMenuInputValue(yAxisColumn, chartPoint.y, allColumnsConfigMap, chartPoint.yRawValues);
    if (!_.isNil(yAxisContextMenuInputValue)) {
        contextMenuInputPoint.pushSelected(yAxisContextMenuInputValue);
    }
    const xAxisColumns = axisConfig.x;
    let xValues = _.get(chartPoint, `series.chart.userOptions.chart.props.xValueToRawValues.${chartPoint.x}`);
    if (xValues) {
        xValues = xValues.map(rawVal => rawVal === null || rawVal === void 0 ? void 0 : rawVal.value);
    }
    let xAxisRawValues = (_a = chartPoint.xRawValues) !== null && _a !== void 0 ? _a : xValues;
    if (!xAxisRawValues && chartPoint.options.selected) {
        xAxisRawValues = [chartPoint.options.x];
    }
    if (xAxisColumns.length !== (xAxisRawValues === null || xAxisRawValues === void 0 ? void 0 : xAxisRawValues.length)) {
        logger.error('mismatch between the lengths of xAxis columns and xAxis raw values');
        return null;
    }
    xAxisColumns.forEach((xAxisColumn, index) => {
        const xAxisContextMenuInputValue = getXAxisContextMenuInputValue(xAxisColumn, chartPoint.x, xAxisRawValues[index], allColumnsConfigMap);
        contextMenuInputPoint.pushSelected(xAxisContextMenuInputValue);
    });
    const yAxisColumns = axisConfig.y;
    const colorColumns = axisConfig.color;
    const isPointGroupedByLegend = yAxisColumns.length > 0 ? yAxisColumns[0].id === yAxisColumn.id : true;
    if (isPointGroupedByLegend && colorColumns.length > 0) {
        const legendColumns = axisConfig.color;
        const legendRawValues = chartPoint.colorRawValues;
        if (legendColumns.length !== legendRawValues.length) {
            logger.error('mismatch between the lengths of legend columns and legend raw values');
            return null;
        }
        legendColumns.forEach((legendColumn, index) => {
            const columnCustomCalendarMap = getColumnConfig(legendColumn, allColumnsConfigMap);
            const contextMenuInputValue = {
                column: legendColumn,
                value: getRawValueForCustomCalendar(columnCustomCalendarMap === null || columnCustomCalendarMap === void 0 ? void 0 : columnCustomCalendarMap.displayToCustomCalendarValueMap, legendColumn, legendRawValues[index]),
            };
            contextMenuInputPoint.pushSelected(contextMenuInputValue);
        });
    }
    const categoryColumn = axisConfig.category[0];
    const categoryValue = chartPoint.categoryName;
    if (xAxisColumns.length >= 1 &&
        xAxisColumns[0] !== categoryColumn &&
        !_.isNil(categoryValue)) {
        const columnCustomCalendarMap = getColumnConfig(categoryColumn, allColumnsConfigMap);
        const contextMenuInputValue = {
            column: categoryColumn,
            value: getRawValueForCustomCalendar(columnCustomCalendarMap.displayToCustomCalendarValueMap, categoryColumn, categoryValue),
        };
        contextMenuInputPoint.pushSelected(contextMenuInputValue);
    }
    return contextMenuInputPoint;
}
export const getContextMenuInputPointFromClickedPoints = (chartModel, clickedPoint, selectedPoints) => {
    const clickContextMenuPoint = getContextMenuInputPoint(chartModel, clickedPoint);
    const hasSelectedPoints = !_.isNil(selectedPoints);
    const selectedPointsContextMenuPoint = hasSelectedPoints
        ? selectedPoints.map(point => getContextMenuInputPoint(chartModel, point))
        : null;
    return {
        clickedPoint: clickContextMenuPoint,
        selectedPoints: selectedPointsContextMenuPoint,
    };
};
export const getContextMenuInputPointKpi = (chartModel, chartPoint, dataModel) => {
    const allColumnsConfigMap = getAllColumnsConfig(chartModel, dataModel);
    const contextMenuInputPoint = new ContextMenuInputPoint();
    const configOptionsForChart = configOptions(chartModel.config.chartType, chartModel);
    const axisConfig = getAxisConfig(chartModel);
    let yAxisColumn;
    if (!yAxisColumn || configOptionsForChart.attributesOnYAxis) {
        yAxisColumn = axisConfig.y[0];
    }
    let yAxisSelectedValue;
    if (yAxisColumn.type === AnswerColumnEffectiveType.Measure) {
        yAxisSelectedValue = chartPoint.value;
    }
    const yAxisContextMenuInputValue = getYAxisContextMenuInputValue(yAxisColumn, chartPoint.value, allColumnsConfigMap, null);
    if (!_.isNil(yAxisContextMenuInputValue)) {
        contextMenuInputPoint.pushSelected(yAxisContextMenuInputValue);
    }
    const xAxisColumns = axisConfig.x;
    xAxisColumns.forEach(xAxisColumn => {
        const xAxisContextMenuInputValue = getXAxisContextMenuInputValue(xAxisColumn, chartPoint.time, convertToMilliSeconds(chartPoint.time), allColumnsConfigMap);
        contextMenuInputPoint.pushSelected(xAxisContextMenuInputValue);
    });
    return contextMenuInputPoint;
};
//# sourceMappingURL=context-menu-point-utils.js.map